import React from 'react';
import styled from 'styled-components';
import {
  DrawerHeader, DrawerTab, DrawerTabs, MetricCard, MetricLabel,
  MetricStrip, MetricSubtext, MetricValue, ModalActions, PanelHeader,
  WorkspacePanel,
} from './shared/CreditWorkspace';

// Presentation only. The existing Loan 360 component owns all data and actions.
const Page = styled.section`
  width: min(100%, 100rem);
  margin: 0 auto;
  padding: 1rem 1.25rem 2.5rem;
  box-sizing: border-box;
  color: #e2e8f0;
  min-width: 0;

  button:focus-visible, a:focus-visible {
    outline: 2px solid #7dd3fc;
    outline-offset: 3px;
  }

  @media (max-width: 760px) { padding: 0.75rem 0.5rem 2rem; }
`;

const ContextHeader = styled.header<{ $sticky: boolean }>`
  position: ${p => p.$sticky ? 'sticky' : 'static'};
  top: 101px;
  z-index: 40;
  border: 0;
  border-radius: 0;
  background: transparent;

  ${DrawerHeader} {
    padding: 0;
    margin-bottom: 0;
    flex-wrap: wrap;
    gap: 1rem;
    align-items: flex-start;
    h1 { font-size: clamp(1.25rem, 1.8vw, 1.65rem) !important; }
  }

  .loan-record-actions { position: relative; margin-left: auto; flex-shrink: 0; }
  .loan-record-actions > summary { padding: 9px 12px; border: 1px solid #334155; border-radius: 6px; cursor: pointer; font-size: .8rem; }
  .loan-record-actions > summary:focus-visible { outline: 2px solid #38bdf8; outline-offset: 3px; }
  .loan-record-actions[open] > ${ModalActions} {
    position: absolute; right: 0; top: 100%; z-index: 50; margin-top: 8px;
    display: grid; width: 240px; padding: 10px; background: #0f172a;
    border: 1px solid #334155; border-radius: 8px; box-shadow: 0 12px 32px #0006;
    button { width: 100%; text-align: left; justify-content: flex-start; }
  }

  ${ModalActions} {
    width: 100%;
    justify-content: flex-start;
    margin-top: 0;
    gap: 0.4rem;
    button { font-size: 0.7rem; padding: 0.48rem 0.65rem; }
  }

  ${MetricStrip} {
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 0;
    padding: 18px 0;
    margin: 18px 0 0;
    border-top: 1px solid #1e293b;
  }

  ${MetricCard} {
    padding: 0 20px; background: transparent; border: 0; border-radius: 0; box-shadow: none;
    border-left: 1px solid #1e293b;
    &:first-child { padding-left: 0; border-left: 0; }
    &::before, &::after { display: none; }
  }
  ${MetricLabel} { font-size: 0.65rem; letter-spacing: 0.04em; }
  ${MetricValue} { font-size: 1.2rem; font-variant-numeric: tabular-nums; }
  ${MetricSubtext} { font-size: 0.7rem; line-height: 1.45; }

  ${DrawerTabs} {
    flex-wrap: nowrap;
    overflow-x: auto;
    margin: 0;
    padding: 0;
    gap: 1.25rem;
    border-top: 0;
    border-bottom: 1px solid #334155;
  }

  ${DrawerTab} {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    flex: 0 0 auto;
    padding: 0.8rem 0;
    border: 0;
    border-bottom: 2px solid transparent;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    font-size: 0.8rem;
    &::before, &::after { display: none; }
    svg { width: 15px; height: 15px; }
    &[aria-current='page'] { border-bottom-color: #38bdf8; color: #e0f2fe; }
  }

  @media (max-width: 1050px) {
    position: static;
    ${MetricStrip} { grid-template-columns: repeat(auto-fit, minmax(145px, 1fr)); row-gap: 16px; }
  }

  @media (max-height: 700px) { position: static; }
`;

const Body = styled.div`
  margin-top: 1.5rem;
  min-width: 0;
  > * + * { margin-top: 1rem; }

  ${WorkspacePanel} {
    border-radius: 14px;
    border-color: rgba(148, 163, 184, 0.16);
    background: rgba(8, 16, 30, 0.78);
    box-shadow: 0 12px 36px rgba(2, 6, 23, 0.14);
  }

  ${PanelHeader} { background: rgba(15, 23, 42, 0.35); }
`;

interface Props {
  stickyHeader?: boolean;
  facilityLabel: string;
  header: React.ReactNode;
  metrics: React.ReactNode;
  navigation: React.ReactNode;
  children: React.ReactNode;
}

const CreditFacilityPage: React.FC<Props> = ({ facilityLabel, header, metrics, navigation, children, stickyHeader = true }) => (
  <Page aria-label={facilityLabel}>
    <ContextHeader $sticky={stickyHeader}>
      {header}
      {metrics}
      <nav aria-label={`${facilityLabel} sections`}>{navigation}</nav>
    </ContextHeader>
    <Body>{children}</Body>
  </Page>
);

export default CreditFacilityPage;
